/** Gemeinsame Quill-Toolbar für E-Mail-Composer */

export const QUILL_TEXT_COLORS = [
  false, // Standardfarbe (Format entfernen)
  '#000000',
  '#1f2937',
  '#4b5563',
  '#9ca3af',
  '#e60000',
  '#ff9900',
  '#ffff00',
  '#008a00',
  '#0066cc',
  '#9933ff',
  '#facccc',
  '#ffebcc',
  '#ffffcc',
  '#cce8cc',
  '#cce0f5',
  '#ebd6ff',
];

export const QUILL_BG_COLORS = [
  false, // Kein Hintergrund
  '#ffff00',
  '#ff9900',
  '#e60000',
  '#008a00',
  '#0066cc',
  '#9933ff',
  '#ffffff',
  '#f3f4f6',
  '#facccc',
  '#ffebcc',
  '#ffffcc',
  '#cce8cc',
  '#cce0f5',
  '#ebd6ff',
];

/** Volle Toolbar: alle wichtigen HTML-Formate für E-Mails */
export const emailComposerToolbar = [
  [{ header: [1, 2, 3, false] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ script: 'sub' }, { script: 'super' }],
  [{ color: QUILL_TEXT_COLORS }, { background: QUILL_BG_COLORS }],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'code'],
  ['clean'],
];

export const emailComposerFormats = [
  'header',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'script',
  'color',
  'background',
  'list',
  'indent',
  'align',
  'blockquote',
  'code-block',
  'code',
  'link',
];

/** Keine Custom-Bindings — Space etc. übernimmt Quill/Browser normal */
export const emailComposerKeyboard = {
  bindings: {},
};

/**
 * React StrictMode / Remount kann eine zweite Toolbar erzeugen.
 * Die alte bleibt sichtbar, ist aber nicht mehr an den Editor gebunden —
 * Bold/Italic/Picker wirken dann tot. Nur die vom Toolbar-Modul
 * referenzierte Toolbar behalten.
 */
export function removeOrphanQuillToolbars(quill) {
  if (!quill || typeof quill.getModule !== 'function') return;
  const toolbarModule = quill.getModule('toolbar');
  const live = toolbarModule?.container;
  const root = quill.container?.parentElement;
  if (!root || !live) return;
  Array.from(root.querySelectorAll(':scope > .ql-toolbar')).forEach((el) => {
    if (el !== live) el.remove();
  });
}

const stickyFormatBound = new WeakSet();

/**
 * Format-Toggles (Bold etc.) ohne vorherige Markierung:
 * Fokus bleibt im Editor, Cursor-Formate greifen beim Weiterschreiben.
 */
export function bindStickyComposerFormats(quill) {
  if (!quill || typeof quill.getModule !== 'function') return;
  if (stickyFormatBound.has(quill)) return;
  const toolbar = quill.getModule('toolbar')?.container;
  if (!toolbar) return;

  stickyFormatBound.add(quill);

  const restoreEditorFocus = () => {
    window.requestAnimationFrame(() => {
      try {
        const range = quill.getSelection();
        quill.focus();
        if (range) {
          quill.setSelection(range.index, range.length, 'silent');
        }
      } catch (_) {
        /* ignore */
      }
    });
  };

  // Mousedown: Fokus nicht an Toolbar-Buttons abgeben (Quill speichert Selection)
  toolbar.addEventListener('mousedown', (e) => {
    const target = e.target.closest('button, .ql-picker-label, .ql-picker-item');
    if (!target || !toolbar.contains(target)) return;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
    e.preventDefault();
  });

  toolbar.addEventListener('click', (e) => {
    const target = e.target.closest('button, .ql-picker-item');
    if (!target || !toolbar.contains(target)) return;
    // Link öffnet Prompt — Fokus nicht sofort zurückzwingen
    if (target.classList.contains('ql-link')) return;
    restoreEditorFocus();
  });
}

/** Orphan-Toolbars entfernen + Sticky-Formate + Link-Tooltip */
export function prepareEmailComposerQuill(quill) {
  removeOrphanQuillToolbars(quill);
  bindStickyComposerFormats(quill);
  bindLinkTooltipDismiss(quill);
  bindEmptyLinkTooltip(quill);
}

const linkTooltipDismissBound = new WeakSet();
const emptyLinkTooltipBound = new WeakSet();

/**
 * Link-Dialog: leeres Eingabefeld (kein Quill-Demo-Placeholder),
 * auch ohne Textmarkierung öffenbar.
 */
export function bindEmptyLinkTooltip(quill) {
  if (!quill || emptyLinkTooltipBound.has(quill)) return;
  const tooltip = quill.theme?.tooltip;
  const toolbar = quill.getModule('toolbar');
  if (!tooltip?.root || !toolbar || typeof toolbar.addHandler !== 'function') return;

  emptyLinkTooltipBound.add(quill);

  if (tooltip.textbox) {
    tooltip.textbox.setAttribute('data-link', '');
    tooltip.textbox.removeAttribute('placeholder');
    tooltip.textbox.placeholder = '';
    tooltip.textbox.value = '';
  }

  toolbar.addHandler('link', function linkHandler(value) {
    if (!value) {
      quill.format('link', false);
      return;
    }
    const range = quill.getSelection(true);
    if (range == null) return;

    let preview = '';
    if (range.length > 0) {
      const selected = String(quill.getText(range) || '').trim();
      // Nur vorbefüllen, wenn die Markierung schon wie eine URL/E-Mail aussieht
      if (/^https?:\/\//i.test(selected) || /^mailto:/i.test(selected)) {
        preview = selected;
      } else if (/^\S+@\S+\.\S+$/.test(selected)) {
        preview = `mailto:${selected}`;
      }
    }

    tooltip.edit('link', preview);
    if (tooltip.textbox) {
      tooltip.textbox.setAttribute('data-link', '');
      tooltip.textbox.placeholder = '';
      if (!preview) tooltip.textbox.value = '';
    }
  });
}

/**
 * Quill schließt das Link-Tooltip nur, wenn der Editor keinen Fokus hat.
 * Zusätzlich: Klick außerhalb / Escape schließt es und leert das Eingabefeld.
 */
export function bindLinkTooltipDismiss(quill) {
  if (!quill || linkTooltipDismissBound.has(quill)) return;
  const tooltip = quill.theme?.tooltip;
  if (!tooltip?.root || typeof tooltip.hide !== 'function') return;

  linkTooltipDismissBound.add(quill);

  const isTooltipOpen = () =>
    tooltip.root && !tooltip.root.classList.contains('ql-hidden');

  const dismissTooltip = () => {
    try {
      if (!isTooltipOpen()) return;
      if (tooltip.textbox) tooltip.textbox.value = '';
      if (typeof tooltip.cancel === 'function') tooltip.cancel();
      else tooltip.hide();
    } catch (_) {
      /* ignore */
    }
  };

  const onPointerDown = (e) => {
    if (!isTooltipOpen()) return;
    const target = e.target;
    if (!(target instanceof Node)) return;
    if (tooltip.root.contains(target)) return;
    // Link-Button in der Toolbar öffnet das Tooltip — nicht sofort schließen
    if (target.closest?.('button.ql-link, .ql-toolbar .ql-link')) return;
    dismissTooltip();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') dismissTooltip();
  };

  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('keydown', onKeyDown, true);
}
